import { CURRENCIES, CURRENCY_INFO, isValidRate, type CurrencyCode } from '@expenditure/core';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useToast } from '../components/Toast';
import { Avatar, ErrorState, Field, PageHeader, PageSkeleton, SectionTitle } from '../components/ui';
import { api } from '../lib/api';
import { errorMessage } from '../lib/format';
import { inviteLink } from '../lib/links';
import { useGroup, useInvalidateGroup } from '../lib/queries';
import { confirmDialog, shareLink } from '../lib/telegram';
import type { Group, Member } from '../lib/types';

export function MembersPage() {
  const { groupId = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidateGroup();
  const { data, isPending, error, refetch } = useGroup(groupId);
  const [guestName, setGuestName] = useState('');

  if (isPending) return <PageSkeleton />;
  if (error) return <ErrorState message={errorMessage(error, t)} onRetry={() => void refetch()} />;

  const { group, me, members } = data;
  const isOwner = me.role === 'owner';
  const isManager = me.role === 'owner' || me.role === 'admin';
  const link = group.inviteCode ? inviteLink(group.inviteCode) : null;

  /** Runs an action; false (with an error toast) if it failed. */
  const run = async (action: () => Promise<unknown>, success?: string): Promise<boolean> => {
    try {
      await action();
      await invalidate(groupId);
      if (success) toast(success);
      return true;
    } catch (err) {
      toast(errorMessage(err, t), 'error');
      return false;
    }
  };

  const share = async () => {
    if (!link) return;
    const result = await shareLink(link, t('members.inviteText', { name: group.name }));
    if (result === 'copied') toast(t('common.copied'));
  };

  const addGuest = (event: FormEvent) => {
    event.preventDefault();
    const name = guestName.trim();
    if (!name) return;
    void run(async () => {
      await api.addGuest(groupId, name);
      setGuestName('');
    });
  };

  const removeMember = async (member: Member) => {
    const self = member.id === me.id;
    if (!(await confirmDialog(self ? t('members.leaveConfirm') : t('members.removeConfirm', { name: member.displayName })))) return;
    // Only leave the page if leaving worked (e.g. not while still owing money).
    if ((await run(() => api.removeMember(groupId, member.id))) && self) void navigate('/groups', { replace: true });
  };

  const deleteGroup = async () => {
    if (!(await confirmDialog(t('members.deleteGroupConfirm')))) return;
    if (await run(() => api.deleteGroup(groupId))) void navigate('/groups', { replace: true });
  };

  return (
    <main className="page page--no-tabbar app">
      <PageHeader title={t('members.title')} />

      <div className="card stack">
        <div>
          <div className="list-title">{t('members.invite')}</div>
          <div className="muted small">{t('members.inviteHint')}</div>
        </div>
        {link && (
          <div className="input small num ellipsis" style={{ background: 'var(--surface-2)' }}>
            {link}
          </div>
        )}
        <div className="row">
          <button className="btn btn--primary grow" onClick={() => void share()}>
            {t('members.shareInvite')}
          </button>
          <button
            className="btn btn--soft"
            onClick={() => link && void navigator.clipboard.writeText(link).then(() => toast(t('common.copied')))}
          >
            {t('common.copyLink')}
          </button>
        </div>
        {isManager && (
          <button
            className="btn btn--ghost btn--small"
            onClick={() =>
              void confirmDialog(t('members.newLinkConfirm')).then(async (ok) => {
                if (ok) await run(() => api.regenerateInvite(groupId));
              })
            }
          >
            ↻ {t('members.newLink')}
          </button>
        )}
      </div>

      <SectionTitle>{t('common.members', { count: members.length })}</SectionTitle>
      <div className="card card--flush list">
        {members.map((m) => {
          const canRemove = m.role !== 'owner' && (m.id === me.id || (isManager && (m.role !== 'admin' || isOwner)));
          return (
            <div className="list-row" key={m.id} style={{ cursor: 'default' }}>
              <Avatar name={m.displayName} photo={m.profilePhoto} />
              <div className="grow">
                <div className="list-title ellipsis">
                  {m.displayName}
                  {m.id === me.id && <span className="muted"> ({t('common.you')})</span>}
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {m.username && <span className="list-sub">@{m.username}</span>}
                  {m.role === 'owner' && <span className="badge badge--primary">{t('members.owner')}</span>}
                  {m.role === 'admin' && <span className="badge badge--primary">{t('members.admin')}</span>}
                  {m.isGuest && <span className="badge">{t('members.guest')}</span>}
                </div>
              </div>
              <div className="stack" style={{ gap: 4, alignItems: 'flex-end' }}>
                {isOwner && !m.isGuest && m.role !== 'owner' && (
                  <button
                    className="btn btn--ghost btn--small"
                    style={{ minHeight: 28 }}
                    onClick={() => void run(() => api.updateMember(groupId, m.id, { role: m.role === 'admin' ? 'member' : 'admin' }))}
                  >
                    {m.role === 'admin' ? t('members.removeAdmin') : t('members.makeAdmin')}
                  </button>
                )}
                {canRemove && m.id !== me.id && (
                  <button
                    className="btn btn--ghost btn--small"
                    style={{ minHeight: 28, color: 'var(--negative)' }}
                    onClick={() => void removeMember(m)}
                  >
                    {t('members.remove')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <form className="card stack" onSubmit={addGuest}>
        <Field label={t('members.addGuest')}>
          <div className="row">
            <input
              className="input grow"
              value={guestName}
              maxLength={64}
              placeholder={t('members.guestPlaceholder')}
              onChange={(e) => setGuestName(e.target.value)}
            />
            <button className="btn btn--soft" disabled={!guestName.trim()}>
              {t('common.add')}
            </button>
          </div>
        </Field>
      </form>

      {isManager && <GroupSettings groupId={groupId} name={group.name} currency={group.currency} rates={group.rates} onSave={run} />}

      {me.role !== 'owner' ? (
        <button className="btn btn--danger btn--block" onClick={() => void removeMember(me)}>
          {t('members.leave')}
        </button>
      ) : (
        <>
          <button className="btn btn--danger btn--block" onClick={() => void deleteGroup()}>
            {t('members.deleteGroup')}
          </button>
          <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>
            {t('members.deleteGroupHint')}
          </p>
        </>
      )}
    </main>
  );
}

function GroupSettings(props: {
  groupId: string;
  name: string;
  currency: CurrencyCode;
  rates: Group['rates'];
  onSave: (action: () => Promise<unknown>, success?: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(props.name);
  const [rates, setRates] = useState<Record<string, string>>(props.rates);
  const [showAll, setShowAll] = useState(false);
  const codes = CURRENCIES.filter((c): c is Exclude<CurrencyCode, 'USD'> => c !== 'USD');
  const rateValid = (code: string) => {
    const value = rates[code] ?? '';
    if (code !== 'KHR') return isValidRate(value);
    const riel = Number(value);
    return Number.isInteger(riel) && riel >= 1000 && riel <= 10000;
  };
  const valid = name.trim().length > 0 && codes.every(rateValid);
  // The rates this group is most likely to need first: its own currency and the riel.
  const main = codes.filter((c) => c === props.currency || c === 'KHR');
  const shown = showAll ? codes : main;

  const save = () => {
    const { KHR, ...others } = rates;
    return api.updateGroup(props.groupId, { name: name.trim(), khrPerUsd: Number(KHR), rates: others });
  };

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) void props.onSave(save, t('common.saved'));
      }}
    >
      <div className="list-title">{t('members.settings')}</div>
      <Field label={t('createGroup.name')}>
        <input className="input" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="field">
        <span className="field-label">{t('createGroup.rate')}</span>
        <div className="rate-list">
          {shown.map((code) => (
            <label className="row rate-row" key={code}>
              <span className="num muted nowrap">$1 =</span>
              <input
                className={`input num ${rateValid(code) ? '' : 'input--invalid'}`}
                inputMode="decimal"
                aria-label={t(`currencies.${code}`)}
                value={rates[code] ?? ''}
                onChange={(e) =>
                  setRates((r) => ({ ...r, [code]: e.target.value.replace(',', '.').replace(code === 'KHR' ? /\D/g : /[^\d.]/g, '') }))
                }
              />
              <span className="num muted rate-code">
                {CURRENCY_INFO[code].symbol} {code}
              </span>
            </label>
          ))}
        </div>
        {!showAll && (
          <button type="button" className="link-button link-button--plain small" onClick={() => setShowAll(true)}>
            {t('members.allRates')}
          </button>
        )}
        <span className="field-hint">{t('members.ratesHint')}</span>
      </div>
      <button className="btn btn--soft" disabled={!valid}>
        {t('common.save')}
      </button>
    </form>
  );
}
